/**
 * User display preferences enums
 * This file contains all the enum definitions used across the user preferences system
 */

// ============================================================================
// THEME & DISPLAY
// ============================================================================

export enum MarginPreferences {
    COMPACT = "compact",
    REGULAR = "regular",
    WIDE = "wide"
}


export enum Theme {
    DARK = "dark",
    LIGHT = "light",
    SYSTEM = "system",
    HOUR_BASED = "hour_based"
}

export enum ThemeVariant {
    NEUTRAL = "neutral",
    STONE = "stone",
    ZINC = "zinc",
    GRAY = "gray",
    SLATE = "slate",
    /** Catppuccin Latte (light) / Frappé (dark); see `globals.css` credit block. */
    PASTEL = "pastel",
}

export enum DetailsDisplayMode {
    DIALOG = "dialog",
    DRAWER = "drawer",
    PANEL = "panel",
    RESIZABLE_PANEL = "resizable_panel",
    PAGE = "page"
}

export enum SearchViewDisplayMode {
    TABLE = "table",
    LIST = "list", // A simple list. If its choosen where lists are usually categorized, multiple lists will be proposed with tabs
    GRID = "grid",
    CALENDAR_DAY = "calendar_day",
    CALENDAR_WEEK = "calendar_week",
    CALENDAR_MONTH = "calendar_month",
    CALENDAR_YEAR = "calendar_year",
    CALENDAR_DAY_RESOURCE = "calendar_day_resource",
    TIMELINE_RESOURCE = "timeline_resource",
    TIMELINE_LIST = "timeline_list",
    TREE = "tree"
}

export enum MenuType {
    SIDEBAR = "sidebar",
    TOPBAR = "topbar"
}

export enum DisplayModeSelectorFormat {
    SELECT = "select",
    BUTTON_GROUP = "button_group",
}

export enum DisplayModeSelectorVisibility {
    TITLE_BAR = "title_bar",
    DISPLAY_PREFERENCES_MENU = "display_preferences_menu",
    HIDDEN = "hidden"
}

export enum LabelFormat {
    TEXT_ONLY = "text_only",
    ICON_ONLY = "icon_only",
    TEXT_WITH_ICON = "text_with_icon",
    ICON_ONLY_WITH_TOOLTIP = "icon_only_with_tooltip",
}

export enum HeaderHeight {
    ONE_LINE = "one_line",
    TWO_LINES = "two_lines",
}

export enum NotificationChannel {
    IN_APP = "in_app",
    EMAIL = "email",
    BOTH = "both",
    NONE = "none"
}

export enum CreateButtonAppearance {
    PLUS_BUTTON = "plus_button",
    CREATE_BUTTON = "create_button",
}

export enum PaginationMode {
    PAGED = "paged",
    INFINITE_SCROLL = "infinite_scroll",
}

export enum TableColumnInteraction {
    DROPDOWN = "dropdown", // Affiche un menu déroulant avec toutes les options possibles
    BUTTONS_HOVER = "buttons_hover", // Affiche un bouton filtre et un boutton tri par défaut
    BUTTONS_ALWAYS_VISIBLE = "buttons_always_visible", // Affiche un bouton filtre et un boutton tri par défaut
    DISABLED = "disabled" // Aucun bouton de filtre ou de tri
}

export enum SearchViewPreferencesApplyMode {
    GLOBAL = "global",
    PER_PAGE = "per_page",
}

// ============================================================================
// NAVIGATION & PAGES
// ============================================================================

export enum AdministrationPage {
    EVENTS = "events",
    CDCS = "cdcs",
    CONTRACTS = "contracts",
    TEACHERS = "teachers",
    SETTINGS = "settings",
    USERS = "users",
    ROLES = "roles",
    SITES = "sites",
    EVENT_TYPES = "event_types",
    TEACHER_STATUSES = "teacher_statuses",
    SKILLS = "skills",
    SEPARATOR = "separator",
}

export enum SitePage {
    EVENTS = "events",
    CDCS = "cdcs",
    CONTRACTS = "contracts",
    TEACHERS = "teachers",
    SETTINGS = "settings",
    DIRECTION = "direction",
    PROCESSES = "processes",
    ADDITIONAL_FEES = "additional_fees",
    EVENT_LOCATIONS = "event_locations",
    FACULTIES = "faculties",
    SITE_USERS = "site_users",
    SEPARATOR = "separator",
}

export enum FacultyPage {
    EVENTS = "events",
    CDCS = "cdcs",
    TEACHERS = "teachers",
    SETTINGS = "settings",
    SEMESTERS = "semesters",
    LEARNING_UNITS = "learning_units",
    PROMOTIONS = "promotions",
    FACULTY_USERS = "faculty_users",
    SEPARATOR = "separator",
}

export enum NavigationTreeMode {
    DISABLED = "disabled",
    ONLY_SCOPES = "only_scopes",
    WORKFLOW = "workflow",
    SIMILAR_TO_TOPBAR = "similar_to_topbar",
    EVERYTHING = "everything",
}

export enum EventCategories {
    PROMOTION = "promotion",
    SITE = "site",
    FACULTY = "faculty",
    EVENT_TYPE = "event_type",
    TEACHER = "teacher",
    DOCUMENTS_STATUS = "documents_status",
}

// ============================================================================
// UI BEHAVIOR & ACCESSIBILITY
// ============================================================================

export enum KeyboardShortbutBehaviour {
    HOLD = "hold",
    TOGGLE = "toggle",
}

export enum KeyboardShortbutBehaviourWithFade {
    HOLD = "hold",
    TOGGLE = "toggle",
    FADE = "fade", // gets displayed and disappears after a while
}

export enum SidebarVariant {
    SIDEBAR = "sidebar",
    FLOATING = "floating",
    INSET = "inset",
}

export enum SidebarCollapsible {
    OFF_CANVAS = "off_canvas",
    ICON = "icon",
    NONE = "none",
}

// ============================================================================
// GRID DISPLAY PRESETS
// ============================================================================
export enum GridColumnsPreset {
    AUTO_200 = "auto_200",
    AUTO_250 = "auto_250",
    AUTO_300 = "auto_300",
    AUTO_400 = "auto_400",
}

// ============================================================================
// DOWNLOAD & EXPORT
// ============================================================================

export enum CdcNamingComponentType {
    CUSTOM_TEXT = "custom_text",
    DOCUMENT_NUMBER = "document_number",
    TEACHER_LASTNAME = "teacher_lastname",
    TEACHER_FIRSTNAME = "teacher_firstname",
    START_DATE = "start_date",
    END_DATE = "end_date",
    FILIERE_NAME = "filiere_name",
    FILIERE_SIGLE = "filiere_sigle",
    SITE_NAME = "site_name",
    SITE_LOCATION = "site_location"
}

export enum ContractNamingComponentType {
    CUSTOM_TEXT = "custom_text",
    DOCUMENT_NUMBER = "document_number",
    TEACHER_LASTNAME = "teacher_lastname",
    TEACHER_FIRSTNAME = "teacher_firstname",
    START_DATE = "start_date",
    END_DATE = "end_date",
    SITE_NAME = "site_name",
    SITE_LOCATION = "site_location"
}

export enum InterventionExportNamingComponentType {
    CUSTOM_TEXT = "custom_text",
    START_DATE = "start_date",
    END_DATE = "end_date"
}

export enum TeacherExportNamingComponentType {
    CUSTOM_TEXT = "custom_text"
}